import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { FollowedSite } from '@app/models/followed-site';
import { CacheService } from '@app/services/cache.service';
import { Environment } from '@environments/interface.environment';
import { List, Site } from '@microsoft/microsoft-graph-types';
import { Endpoint } from '@microsoft/microsoft-graph-types-beta';
import { concat, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SiteService {

  constructor(private http: HttpClient, private cache: CacheService, @Inject(CUSTOMERENVIRONMENT) private environment: Environment, private httpHelper: HttpHelper) { }

  private readonly cacheKeyPrefix = 'SiteService';

  public useCache = true;

  private getSiteInfoFromUrl$ = new Map<string, Observable<Site>>();
  private getGroupEndpoints$ = new Map<string, Observable<Array<Endpoint>>>();
  private findSitesResult$ = new Map<string, Observable<Array<Site>>>();
  private getHubSiteId$ = new Map<string, Observable<string>>();

  // Takes a URL (e.g. a aspx page) and queries MS Graph for information about the site collection hosting the artifact
  getSiteInfoFromUrl(url: string): Observable<Site> {

    let endPoint = '/sites/root';
    if (url.toLowerCase().indexOf('sites') !== -1 || url.toLowerCase().indexOf('teams') !== -1) {
      const matches = url.match(/https:\/\/(.*?)\/(sites|teams)\/(.*?)\//i);  // matches: [1] = tenant, [2] = managed path, [3] = site collection
      endPoint = '/sites/' + matches[1] + ':/' + matches[2] + '/' + matches[3]; // endPoint should be something like this: /sites/booldev.sharepoint.com:/sites/TechLunchCommSite
    }

    if (this.getSiteInfoFromUrl$.has(endPoint)) {
      return this.getSiteInfoFromUrl$.get(endPoint);
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-siteInfo-${endPoint}`;
      const httpParams = new HttpParams()
        .append('$select', `displayName,webUrl`);

      const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}${endPoint}`, { params: httpParams })
        .pipe(
          map(response => {
            const result = response as Site;
            this.useCache && this.cache.set(cacheKey, result);
            return result;
          }),
          shareReplay({ bufferSize: 1, refCount: true })
        );
      this.getSiteInfoFromUrl$.set(endPoint, fetchOperation);

      if (this.useCache) {
        const cachedData = this.cache.get<Site>(cacheKey);
        if (cachedData) {
          this.getSiteInfoFromUrl$.set(endPoint, of(cachedData));
        }
      }

      return this.getSiteInfoFromUrl$.get(endPoint);
    }

  }

  public getFollowedSites(count: number): Observable<Array<FollowedSite>> {

    const cacheKey = `${this.cacheKeyPrefix}-followedSites-${count}`;

    const httpParams = new HttpParams()
      .append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl))
      .append('start', '0')
      .append('count', count.toString());

    const fetchOperation = this.http.get<any>(`${Constants.API_ENDPOINT}/follow/sites`, {
      params: httpParams,
    })
      .pipe(
        map(data => {
          console.log(`fetched followed sites: `);
          console.log(data);
          const results = data.Items as FollowedSite[];
          this.useCache && this.cache.set(cacheKey, results);
          return results;
        }),
        catchError(this.httpHelper.handleError({ operation: 'getFollowedSites', result: new Array<FollowedSite>() }))
      );

    if (this.useCache) {
      const cachedData = this.cache.get<Array<FollowedSite>>(cacheKey);
      if (cachedData) {
        return of(cachedData);
      }
    }
    return fetchOperation;

  }

  public getGroupEndpoints(groupId: string): Observable<Array<Endpoint>> {
    const cacheKey = `${this.cacheKeyPrefix}-getGroupEndpoints-${groupId}`;

    if (this.getGroupEndpoints$.has(groupId)) {
      return this.getGroupEndpoints$.get(groupId);
    } else {
      const fetchOperation = this.http
        .get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_BETA_VERSION}/groups/${groupId}/endpoints`)
        .pipe(
          map(result => {
            const r = result.value as Array<Endpoint>;
            this.useCache && this.cache.set(cacheKey, r);
            return r;
          }),
          catchError(this.httpHelper.handleError({ operation: 'getGroupEndpoints', result: null, rethrow: true })),
          shareReplay({ bufferSize: 1, refCount: true }));

      this.getGroupEndpoints$.set(groupId, fetchOperation);
    }

    if (this.useCache) {
      const cachedData = this.cache.get<Array<Endpoint>>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), this.getGroupEndpoints$.get(groupId));
      }
    }

    return this.getGroupEndpoints$.get(groupId);

  }

  public findSites(term: string): Observable<Array<Site>> {
    // https://graph.microsoft.com/v1.0/sites?search=*
    const httpParams = new HttpParams()
      .append('search', term);

    const cacheKey = `${this.cacheKeyPrefix}-findSites-${term}`;

    if (this.findSitesResult$.has(term)) {
      return this.findSitesResult$.get(term);
    } else {
      const fetchOperation = this.http
        .get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/sites`, { params: httpParams })
        .pipe(
          map(result => {
            const r = result.value as Array<Site>;
            this.useCache && this.cache.set(cacheKey, r);
            return r;
          }),
          catchError(this.httpHelper.handleError({ operation: 'findSites', result: null, rethrow: true })),
          shareReplay({ bufferSize: 1, refCount: true }));

      this.findSitesResult$.set(term, fetchOperation);
    }

    if (this.useCache) {
      const cachedData = this.cache.get<Array<Site>>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), this.findSitesResult$.get(term));
      }
    }

    return this.findSitesResult$.get(term);

  }

  public getLists(siteId: string): Observable<Array<List>> {
    return this.http
      .get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/sites/${siteId}/lists?$select=system,name,id,drive&$expand=drive`)
      .pipe(
        map(result => {
          const r = result.value as Array<List>;
          return r;
        }),
        catchError(this.httpHelper.handleError({ operation: 'getLists', result: [], rethrow: true }))
      );
  }

  /** Returns the id of the site specified in the hub if it is a hub-site, otherwise null */
  getHubSiteId(url: string): Observable<string> {
    const cacheKey = `${this.cacheKeyPrefix}-GetHubSiteId-${url}`;

    if (this.getHubSiteId$.has(cacheKey)) {
      return this.getHubSiteId$.get(cacheKey);
    } else {
      this.getHubSiteId$.set(cacheKey, this.http.get<any>(`${url}/_api/site?$select=IsHubSite,HubSiteId`)
        .pipe(
          map(siteInfo => siteInfo.IsHubSite ? siteInfo.HubSiteId : null),
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getHubSiteId', result: '', cacheKey: cacheKey })),
          shareReplay({ bufferSize: 1, refCount: true })
        )
      );

      if (this.useCache) {
        const cachedData = this.cache.get<string>(cacheKey);
        if (cachedData) {
          return of(cachedData);
        }
      }

      return this.getHubSiteId$.get(cacheKey);
    }
  }

}
